import React, { useState } from 'react';
import { 
  Search, Filter, Plus, Edit, Eye, MapPin, BarChart3,
  Package, AlertTriangle, CheckCircle, Clock, RefreshCw,
  Download, Upload, Scan, ArrowUpRight, ArrowDownLeft
} from 'lucide-react';

const InventoryManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const inventory = [
    {
      id: '1',
      sku: 'CT-001',
      name: 'Premium Cotton T-Shirt',
      category: 'T-Shirts',
      currentStock: 150,
      reservedStock: 25,
      availableStock: 125,
      reorderLevel: 50,
      location: 'Zone A - Shelf 12',
      binLocation: 'A12-001',
      lastMovement: '2024-02-28',
      movementType: 'received',
      unitCost: 15.00,
      totalValue: 2250.00,
      status: 'in_stock',
      supplier: 'Cotton Mills Ltd.'
    },
    {
      id: '2',
      sku: 'PS-002',
      name: 'Cotton Polo Shirt',
      category: 'Polo Shirts',
      currentStock: 15,
      reservedStock: 10,
      availableStock: 5,
      reorderLevel: 30,
      location: 'Zone A - Shelf 15',
      binLocation: 'A15-003',
      lastMovement: '2024-02-27',
      movementType: 'shipped',
      unitCost: 18.50,
      totalValue: 277.50,
      status: 'critical',
      supplier: 'Premium Textiles'
    },
    {
      id: '3',
      sku: 'CP-003',
      name: 'Casual Cotton Pants',
      category: 'Pants',
      currentStock: 89,
      reservedStock: 15,
      availableStock: 74,
      reorderLevel: 40,
      location: 'Zone B - Shelf 8',
      binLocation: 'B08-002',
      lastMovement: '2024-02-26',
      movementType: 'received',
      unitCost: 22.00,
      totalValue: 1958.00,
      status: 'in_stock',
      supplier: 'Fashion Forward Co.'
    },
    {
      id: '4',
      sku: 'CH-004',
      name: 'Premium Cotton Hoodie',
      category: 'Hoodies',
      currentStock: 0,
      reservedStock: 0,
      availableStock: 0,
      reorderLevel: 25,
      location: 'Zone B - Shelf 20',
      binLocation: 'B20-001',
      lastMovement: '2024-02-20',
      movementType: 'shipped',
      unitCost: 28.00,
      totalValue: 0,
      status: 'out_of_stock',
      supplier: 'Comfort Wear Ltd.'
    },
    {
      id: '5',
      sku: 'CS-005',
      name: 'Cotton Casual Shirt',
      category: 'Shirts',
      currentStock: 45,
      reservedStock: 5,
      availableStock: 40,
      reorderLevel: 30,
      location: 'Zone A - Shelf 18',
      binLocation: 'A18-004',
      lastMovement: '2024-02-25',
      movementType: 'received',
      unitCost: 16.50,
      totalValue: 742.50,
      status: 'low_stock',
      supplier: 'Classic Shirts Inc.'
    }
  ];

  const zones = ['Zone A', 'Zone B', 'Zone C', 'Zone D'];

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesZone = filterZone === 'all' || item.location.includes(filterZone);
    
    return matchesSearch && matchesStatus && matchesZone;
  });

  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === filteredInventory.length 
        ? [] 
        : filteredInventory.map(item => item.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          In Stock
        </span>;
      case 'low_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Low Stock
        </span>;
      case 'critical':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Critical
        </span>;
      case 'out_of_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Package className="h-3 w-3 mr-1" />
          Out of Stock
        </span>;
      default:
        return null;
    }
  };

  const getMovementIcon = (type: string) => {
    return type === 'received' ? (
      <ArrowDownLeft className="h-4 w-4 text-green-500" />
    ) : (
      <ArrowUpRight className="h-4 w-4 text-blue-500" />
    );
  };

  const totalValue = inventory.reduce((sum, item) => sum + item.totalValue, 0);
  const lowStockItems = inventory.filter(item => item.status === 'low_stock' || item.status === 'critical').length;
  const outOfStockItems = inventory.filter(item => item.status === 'out_of_stock').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
              <p className="text-gray-600">Track and manage warehouse inventory</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Scan className="h-4 w-4" />
                <span>Scan Item</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Item</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by item name, SKU, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
                <option value="critical">Critical</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Zones</option>
                {zones.map(zone => (
                  <option key={zone} value={zone}>{zone}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{inventory.reduce((sum, item) => sum + item.currentStock, 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">${totalValue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-gray-900">{lowStockItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Package className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                <p className="text-2xl font-bold text-gray-900">{outOfStockItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Inventory Items ({filteredInventory.length})</h2>
              {selectedItems.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedItems.length} selected</span>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Bulk Update</button>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Move Items</button>
                </div>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === filteredInventory.length && filteredInventory.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Levels</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Movement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInventory.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                        <div className="text-xs text-gray-400">{item.category}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          Current: {item.currentStock}
                        </div>
                        <div className="text-xs text-gray-500">
                          Available: {item.availableStock}
                        </div>
                        <div className="text-xs text-gray-500">
                          Reserved: {item.reservedStock}
                        </div>
                        <div className="text-xs text-gray-400">
                          Reorder: {item.reorderLevel}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1 text-sm text-gray-900">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span>{item.location}</span>
                      </div>
                      <div className="text-xs text-gray-500">{item.binLocation}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getMovementIcon(item.movementType)}
                        <div>
                          <div className="text-sm text-gray-900 capitalize">{item.movementType}</div>
                          <div className="text-xs text-gray-500">{item.lastMovement}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        ${item.totalValue.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Unit: ${item.unitCost}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-gray-600" title="View Details">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Move">
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="History">
                          <BarChart3 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;